import axios, { AxiosError } from 'axios'

/**
 * The app's single HTTP client.
 * - Relative baseURL: the container's nginx proxies /api to api-wp-one (same origin).
 * - withCredentials: the JWT lives in an HttpOnly cookie. Never read/store tokens in JS.
 */
export const http = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  headers: { Accept: 'application/json' },
  timeout: 20_000,
})

/** api-wp-one standard envelope: { data, meta, errors }. */
export interface Envelope<T> {
  data: T
  meta?: Record<string, unknown> | null
  errors?: unknown[]
}

/** One item of `errors` in the api-wp-one envelope. */
export interface ApiErrorItem {
  code: string
  message: string
  field?: string | null
}

/** Error already translated for the UI (message is user-facing, in Portuguese). */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
    readonly detail?: unknown,
    /** Readable code from the API (`errors[0].code`), e.g. 'sessao_encerrada_novo_login'. */
    readonly code: string | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// User-facing messages (Portuguese).
const MESSAGES: Record<number, string> = {
  401: 'Sua sessão expirou. Entre novamente.',
  403: 'Você não tem acesso a esta área.',
  404: 'Não encontramos o que você procurou.',
  409: 'Este registro foi alterado por outra pessoa. Atualize a página.',
  422: 'Confira os dados informados.',
  429: 'Muitas tentativas. Aguarde um pouco e tente de novo.',
}

function firstError(body: unknown): ApiErrorItem | undefined {
  if (
    typeof body !== 'object' ||
    body === null ||
    !('errors' in body) ||
    !Array.isArray(body.errors)
  )
    return undefined
  const item: unknown = body.errors[0]
  if (typeof item !== 'object' || item === null) return undefined
  const { code, message } = item as Record<string, unknown>
  return typeof code === 'string' && typeof message === 'string'
    ? (item as ApiErrorItem)
    : undefined
}

/**
 * The API's own message is shown only when it is written for people: a readable code
 * (e.g. 'sessao_expirada_inatividade') or an explicit `detail` (e.g. 'CPF/e-mail ou senha inválidos').
 * Bare status codes ('401' + 'Unauthorized') and validation errors (Pydantic, in English)
 * fall back to the Portuguese message for the status.
 */
function isHumanMessage(item: ApiErrorItem, status: number | null): boolean {
  if (item.code === 'validation_error' || item.code === 'internal_error') return false
  if (item.code === String(status)) return !/^[A-Z][a-z]+( [A-Z][a-z]+)*$/.test(item.message)
  return true
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error
  if (error instanceof AxiosError) {
    const status = error.response?.status ?? null
    const body: unknown = error.response?.data
    const item = firstError(body)
    const code = item && item.code !== String(status) ? item.code : null
    const message =
      (item && isHumanMessage(item, status) ? item.message : undefined) ??
      (status ? MESSAGES[status] : undefined) ??
      (status === null
        ? 'Sem conexão com o servidor. Verifique sua internet.'
        : 'Algo deu errado. Tente de novo.')
    return new ApiError(message, status, body, code)
  }
  return new ApiError('Algo deu errado. Tente de novo.', null, error)
}

/** GET that unwraps the envelope and normalizes errors. */
export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  try {
    const response = await http.get<Envelope<T>>(url, { params })
    return response.data.data
  } catch (error) {
    throw toApiError(error)
  }
}

export async function post<T, B = unknown>(url: string, body?: B): Promise<T> {
  try {
    const response = await http.post<Envelope<T>>(url, body)
    return response.data.data
  } catch (error) {
    throw toApiError(error)
  }
}

export async function patch<T, B = unknown>(url: string, body?: B): Promise<T> {
  try {
    const response = await http.patch<Envelope<T>>(url, body)
    return response.data.data
  } catch (error) {
    throw toApiError(error)
  }
}
